import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChartBarIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from '@/components/ui/Table';
import { useApi } from '@/hooks/useApi';
import { apiClient } from '@/lib';
import type { BooksListResponse, PublishersListResponse } from '@/types/api';

interface PublisherLookupResponse {
  pub_id: string;
  pub_name: string;
}

export default function DistPublishersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPublisherName, setNewPublisherName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build API endpoint with query params
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    params.set('pageSize', '50');
    const queryString = params.toString();
    return `distribution/publishers${queryString ? `?${queryString}` : ''}`;
  }, [debouncedSearch]);

  // Fetch publishers from API
  const { data, isLoading, error, refetch } = useApi<PublishersListResponse>(apiEndpoint);

  // Update URL search params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, setSearchParams]);

  const publishers = data?.items || [];
  const total = data?.total || 0;

  // Per-publisher stats loaded from Distribution Worker via backend proxy
  const [rowStats, setRowStats] = useState<
    Record<string, { totalBooks: number; published: number; pending: number; failed: number }>
  >({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const handleCreatePublisher = async () => {
    const trimmed = newPublisherName.trim();
    if (!trimmed) {
      setCreateError('Publisher name is required');
      return;
    }

    try {
      setCreateLoading(true);
      setCreateError(null);

      await apiClient.post('distribution/publishers/simple-create', {
        pub_name: trimmed,
      });

      setShowAddModal(false);
      setNewPublisherName('');
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create publisher';
      setCreateError(message);
    } finally {
      setCreateLoading(false);
    }
  };

  // Fetch stats for each publisher in the current list
  useEffect(() => {
    let isCancelled = false;

    async function fetchStatsForPublishers() {
      if (!publishers.length) return;

      const names = publishers
        .map((p) => p.name || p.pub_name)
        .filter((n): n is string => !!n);

      // Skip if we already have stats for every publisher
      const missingNames = names.filter((name) => !rowStats[name]);
      if (!missingNames.length) return;

      try {
        setStatsLoading(true);
        setStatsError(null);

        const updates: Record<
          string,
          { totalBooks: number; published: number; pending: number; failed: number }
        > = {};

        for (const pubName of missingNames) {
          try {
            // 1) Resolve pub_id from pub_name
            const lookup = await apiClient.post<PublisherLookupResponse>(
              'distribution/publishers/lookup-id',
              { pub_name: pubName }
            );

            if (isCancelled) return;

            const pubId = lookup.pub_id;
            if (!pubId) {
              console.warn('No pub_id returned for publisher', pubName);
              continue;
            }

            const base = `distribution/books?publisherId=${encodeURIComponent(pubId)}`;

            // 2) Fetch totals per status
            const [allRes, publishedRes, pendingRes, failedRes] = await Promise.all([
              apiClient.get<BooksListResponse>(base),
              apiClient.get<BooksListResponse>(`${base}&status=PUBLISHED`),
              apiClient.get<BooksListResponse>(`${base}&status=PENDING`),
              apiClient.get<BooksListResponse>(`${base}&status=FAILED`),
            ]);

            if (isCancelled) return;

            updates[pubName] = {
              totalBooks: allRes.total ?? 0,
              published: publishedRes.total ?? 0,
              pending: pendingRes.total ?? 0,
              failed: failedRes.total ?? 0,
            };
          } catch (err) {
            if (isCancelled) return;
            console.error('Failed to load stats for publisher', pubName, err);
            // Keep going for other publishers; record a generic error
            setStatsError('Some publisher statistics could not be loaded');
          }
        }

        if (!isCancelled && Object.keys(updates).length) {
          setRowStats((prev) => ({ ...prev, ...updates }));
        }
      } finally {
        if (!isCancelled) {
          setStatsLoading(false);
        }
      }
    }

    fetchStatsForPublishers();

    return () => {
      isCancelled = true;
    };
  }, [publishers, rowStats]);

  // Calculate aggregate stats for header cards
  const stats = useMemo(() => {
    const activeCount = publishers.filter((p) => p.active !== false).length;

    let totalBooks = 0;
    let totalPublished = 0;

    publishers.forEach((p) => {
      const key = p.name || p.pub_name;
      const perPublisher = key ? rowStats[key] : undefined;

      if (perPublisher) {
        totalBooks += perPublisher.totalBooks;
        totalPublished += perPublisher.published;
      } else {
        totalBooks += p.books_count || 0;
        totalPublished += p.published_count || 0;
      }
    });

    return {
      total,
      active: activeCount,
      totalBooks,
      totalPublished,
    };
  }, [publishers, total, rowStats]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Publishers</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage publisher accounts and configurations
            {total > 0 && <span className="ml-2 text-slate-500 dark:text-slate-400">({total} publishers)</span>}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowAddModal(true)}>
            Add Publisher
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.total}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">Total Publishers</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-success-500">{stats.active}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">Active</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.totalBooks}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">Total Books</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-primary-500">{stats.totalPublished}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">Published</p>
          </CardBody>
        </Card>
      </div>

      {statsLoading && (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading publisher statistics...</p>
      )}
      {statsError && !statsLoading && (
        <p className="text-sm text-danger-500">{statsError}</p>
      )}

      {/* Add Publisher Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          if (createLoading) return;
          setShowAddModal(false);
          setCreateError(null);
        }}
        title="Add Publisher"
        description="Create a new publisher in the Distribution system."
        size="sm"
      >
        <ModalBody>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Publisher Name
            </label>
            <Input
              placeholder="Enter publisher name"
              value={newPublisherName}
              onChange={(e) => setNewPublisherName(e.target.value)}
              autoFocus
            />
            {createError && (
              <p className="text-sm text-danger-500">{createError}</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (createLoading) return;
              setShowAddModal(false);
              setCreateError(null);
            }}
            disabled={createLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePublisher}
            isLoading={createLoading}
            disabled={createLoading || !newPublisherName.trim()}
          >
            Add Publisher
          </Button>
        </ModalFooter>
      </Modal>

      {/* Search */}
      <Card padding="md">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search publishers by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Failed to load publishers</h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-600 dark:text-slate-300 mt-4">Loading publishers...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && publishers.length === 0 && (
        <EmptyState
          icon={<BuildingOfficeIcon className="w-12 h-12" />}
          title="No publishers found"
          description={
            debouncedSearch
              ? 'Try adjusting your search'
              : 'Get started by adding your first publisher'
          }
          action={
            debouncedSearch ? (
              <Button variant="outline" onClick={() => setSearch('')}>
                Clear Search
              </Button>
            ) : (
              <Button
                leftIcon={<PlusIcon className="w-4 h-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Add Publisher
              </Button>
            )
          }
        />
      )}

      {/* Publishers Table */}
      {!error && publishers.length > 0 && (
        <>
          <Card padding="none">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow isHoverable={false}>
                    <TableHeaderCell>Publisher</TableHeaderCell>
                    <TableHeaderCell>Total Books</TableHeaderCell>
                    <TableHeaderCell>Published</TableHeaderCell>
                    <TableHeaderCell>Pending</TableHeaderCell>
                    <TableHeaderCell>Failed</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publishers.map((publisher) => {
                    const displayName = publisher.name || publisher.pub_name || '—';
                    const perPublisherStats = rowStats[displayName];

                    return (
                    <TableRow key={publisher.id ?? publisher.pub_name}>
                      <TableCell>
                        <Link
                          to={`/distribution/publishers/${publisher.id}`}
                          state={{ pubName: publisher.name || publisher.pub_name }}
                          className="font-medium text-slate-900 dark:text-slate-100 hover:text-primary-500"
                        >
                          {publisher.name || publisher.pub_name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">
                        {perPublisherStats
                          ? perPublisherStats.totalBooks
                          : publisher.books_count ?? '—'}
                      </TableCell>
                      <TableCell className="text-success-600">
                        {perPublisherStats
                          ? perPublisherStats.published
                          : publisher.published_count ?? '—'}
                      </TableCell>
                      <TableCell className="text-warning-600">
                        {perPublisherStats
                          ? perPublisherStats.pending
                          : publisher.pending_count ?? '—'}
                      </TableCell>
                      <TableCell className="text-danger-600">
                        {perPublisherStats
                          ? perPublisherStats.failed
                          : publisher.failed_count ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={publisher.active !== false ? 'success' : 'warning'}>
                          {publisher.active !== false ? 'active' : 'inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Link
                            to={`/distribution/publishers/${publisher.id}`}
                            state={{ pubName: publisher.name || publisher.pub_name }}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={<PencilIcon className="w-3 h-3" />}
                            >
                              Edit
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<ChartBarIcon className="w-3 h-3" />}
                            disabled
                          >
                            Stats
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Footer Info */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Showing {publishers.length} of {total} publishers
              {isLoading && <span className="ml-2">(refreshing...)</span>}
            </p>
            <Button variant="outline" size="sm" disabled>
              Export
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
